import { BASE_TOKEN_CONFIG } from '@/client/confs/app';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * DPIN utility band (ported from AIP's aip-utility-band, ADR:
 * dpin-redesign-design.md §4/§5). Contained mid-page card between "Trending
 * now" and "Browse markets": dual-token collateral narrative (left) + two
 * glowing DPS/DPN marks (right, from the live base-token config -- not
 * hardcoded) + 3 qualitative feature cells in an asymmetric split (lead
 * settlement cell spanning two rows + liquidity/collateral stacked right,
 * single column under 768px -- .dpin-features in dpin-gold.css). Reuses the
 * shared .mcard-neon surface and the .aip-hero-* / .aip-feature-* classes
 * ported in Step 1.
 */
export const DpinUtilityBand = memo(() => {
  const { t } = useTranslation(['home']);

  // Empty string in a locale = "no trailing text" (mirrors AIP's hero-title-2
  // pattern) -- e.g. zh-hk needs a trailing verb ("...交易") that English doesn't.
  const titleSuffix = t('dpin-band-title-suffix', '');

  return (
    <section className="px-4 md:px-6 py-8" id="dpin-utility-band">
      <div className="mcard-neon aip-band">
        <div className="aip-hero-grid">
          <div className="aip-hero-copy">
            <div className="neon-badge">{t('dpin-band-eyebrow', 'Dual collateral')}</div>
            <h2 className="aip-band-h1">
              {t('dpin-band-title-1', 'Trade with ')}
              <span className="neon">{t('dpin-band-title-neon', 'DPS & DPN')}</span>
              {titleSuffix && <>{titleSuffix}</>}
            </h2>
            <p className="aip-hero-sub">
              {t(
                'dpin-band-sub',
                "Every market on DPIN Predict is collateralized in one of the network's native tokens. Pick a side in DPS or DPN. Positions redeem 1:1 at settlement."
              )}
            </p>
          </div>

          <div className="dpin-coin-stage" aria-hidden>
            {BASE_TOKEN_CONFIG.map(token => (
              <div className="dpin-coin" key={token.address}>
                <div className="dpin-coin-glow" />
                <token.icon />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Qualitative feature cells, content per design doc §4. Asymmetric
          composition instead of a flat 3-up row: the settlement cell leads and
          carries the hero's gold grid motif (.dpin-feature-scene); the
          collateral cell echoes the band's coin stage with the live token
          marks in place of a generic glyph. */}
      <div className="dpin-features">
        <div className="aip-feature dpin-feature-lead mcard-neon mcard-surface">
          <div className="dpin-feature-scene" aria-hidden />
          <span className="aip-feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l7 3v5c0 4.5-2.9 7.6-7 9-4.1-1.4-7-4.5-7-9V6l7-3z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </span>
          <div className="aip-feature-title">{t('feature-settlement-title', 'On-chain settlement')}</div>
          <p className="aip-feature-desc">
            {t('feature-settlement-desc', 'Outcomes resolve through decentralized oracles with a public dispute window.')}
          </p>
        </div>

        <div className="aip-feature mcard-neon mcard-surface">
          <span className="aip-feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </span>
          <div className="aip-feature-title">{t('feature-liquidity-title', 'Instant liquidity')}</div>
          <p className="aip-feature-desc">
            {t('feature-liquidity-desc', 'An automated market maker keeps every market tradable around the clock.')}
          </p>
        </div>

        <div className="aip-feature mcard-neon mcard-surface">
          <span className="dpin-feature-tokens" aria-hidden>
            {BASE_TOKEN_CONFIG.map(token => (
              <token.icon key={token.address} />
            ))}
          </span>
          <div className="aip-feature-title">{t('feature-collateral-title', 'Dual-token collateral')}</div>
          <p className="aip-feature-desc">
            {t('feature-collateral-desc', 'Markets settle in DPS or DPN, the tokens you already hold on DPIN.')}
          </p>
        </div>
      </div>
    </section>
  );
});

DpinUtilityBand.displayName = 'DpinUtilityBand';
